/**
 * Cross-entity aggregator: combines `getFoldersWithGuidelines`
 * (entities/guideline) with `useCurrentWorkspaceId` (entities/workspace) to
 * produce the workspace-scoped folders+guidelines list. FSD prohibits
 * cross-entity value imports between sibling entity slices, so this
 * aggregator MUST live above the entity layer.
 *
 * Putting it in `widgets/` would block features from consuming it (features →
 * widgets is also banned), so `shared/` is the pragmatic home. The
 * `shared/hooks/useFoldersWithGuidelines/` directory is classified under the
 * `shared-cross-entity-aggregator` element type in `eslint.config.js`, which
 * grants it (and ONLY it) `shared → entities` value imports.
 *
 * Mirrors the pattern established by `useWorkspaceAuth` and
 * `useKnowledgeBasesList`.
 *
 * Lifted from `features/guidelines/hooks/useFoldersWithGuidelines.ts`
 * (re-exported from old path for backward compat).
 */
import { useQuery } from '@tanstack/react-query';

import { getFoldersWithGuidelines } from '@/entities/guideline';
import { useCurrentWorkspaceId } from '@/entities/workspace';

/**
 * Hook to fetch folders with their guidelines for the current workspace.
 * Returns folders with nested guideline summaries in a single query.
 *
 * @param enabled - Whether the query should be enabled (default: true)
 * @returns TanStack Query result with folders and guidelines data
 */
export const useFoldersWithGuidelines = (enabled = true) => {
  const workspaceId = useCurrentWorkspaceId();

  return useQuery({
    queryKey: ['folders-with-guidelines', workspaceId],
    queryFn: () => getFoldersWithGuidelines(workspaceId ?? ''),
    enabled: enabled && !!workspaceId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};
